import FlagEmoji from "@/components/ui/FlagEmoji";
import { useState, useEffect, useCallback } from "react";
import SEOHead from "@/components/SEOHead";
import { Link, useNavigate } from "react-router-dom";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import LivePlatformActivity from "@/components/home/LivePlatformActivity";
import AnimatedRouteMap from "@/components/home/AnimatedRouteMap";
import MeetMalak from "@/components/home/MeetMalak";
import { useQuote } from "@/contexts/QuoteContext";
import { Button } from "@/components/ui/button";
import { useScrollReveal, staggerStyle } from "@/hooks/useScrollReveal";

import { AnimatedNumber } from "@/components/AnimatedNumber";
import {
  ArrowRight,
  ShieldCheck,
  Shield,
  Lock,
  Eye,
  Scale,
  Globe,
  Route,
  Headphones,
  ChevronRight,
  ChevronDown,
  Package,
  PackageCheck,
  Search,
  CalendarCheck,
  Truck,
  BadgeCheck,
  Users,
  MapPin,
  CheckCircle,
  HelpCircle,
  Clock,
  Wallet,
  Target,
  UserCheck,
  LayoutDashboard,
  Store,
  ArrowLeftRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useCountryDirectionLogic } from "@/hooks/useCountryDirectionLogic";
import { useTranslation } from "react-i18next";
import { NeonTracerIcon } from "@/components/ui/NeonTracerIcon";
import LaunchBanner from "@/components/LaunchBanner";

/* ─── ROTATING WORDS ─── */
const rotatingWordKeys = [
  { key: "hero.rotatingWords.home", color: "text-brand-orange" },
  { key: "hero.rotatingWords.securely", color: "text-brand-sky" },
  { key: "hero.rotatingWords.simply", color: "text-brand-teal" },
  { key: "hero.rotatingWords.safely", color: "text-brand-sky" },
  { key: "hero.rotatingWords.withCare", color: "text-brand-orange" },
];

const RotatingWord = () => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex((i) => (i + 1) % rotatingWordKeys.length);
        setVisible(true);
      }, 400);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  return (
    <span
      className={cn(
        `inline-block ${rotatingWordKeys[index].color} transition-all duration-brand-slow ease-brand`,
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
      )}
    >
      {t(rotatingWordKeys[index].key)}
    </span>
  );
};

/* ─── DIRECTION BADGE ─── */
const DirectionBadge = () => {
  const { t } = useTranslation();
  const [europeFirst, setEuropeFirst] = useState(true);

  useEffect(() => {
    const id = setInterval(() => setEuropeFirst((v) => !v), 4000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="section-badge-dark">
      <span
        className={cn(
          "text-sm font-semibold tracking-wide transition-opacity duration-brand-mid ease-brand",
          europeFirst ? "text-white/90" : "text-white/50"
        )}
      >
        {t('hero.europeToMorocco')}
      </span>
      <span className="text-white/20">|</span>
      <span
        className={cn(
          "text-sm font-semibold tracking-wide transition-opacity duration-brand-mid ease-brand",
          !europeFirst ? "text-white/90" : "text-white/50"
        )}
      >
        {t('hero.moroccoToEurope')}
      </span>
    </div>
  );
};


/* ─── QUICK QUOTE WIDGET ─── */
const QuickQuoteWidget = () => {
  const { t } = useTranslation();
  const { localePath } = useLocalizedPath();
  const navigate = useNavigate();
  const { setQuoteData } = useQuote();
  const { getPickupCountries, getDeliveryCountries, translateCountry, countries } = useCountryDirectionLogic();
  const isoOf = (name: string) => countries.find((c) => c.name === name)?.isoCode?.toLowerCase();

  const [pickup, setPickup] = useState("");
  const [delivery, setDelivery] = useState("");

  const pickupCountryNames = Object.keys(getPickupCountries());
  const deliveryCountryNames = Object.keys(getDeliveryCountries(pickup));

  const handlePickup = (c: string) => {
    setPickup(c);
    setDelivery("");
  };

  const canSwap = !!pickup && !!delivery && Object.keys(getDeliveryCountries(delivery)).includes(pickup);
  const handleSwap = () => {
    if (!canSwap) return;
    const p = pickup;
    setPickup(delivery);
    setDelivery(p);
  };

  const chip = (c: string, selected: boolean, onClick: () => void) => (
    <button key={c} type="button" onClick={onClick} aria-pressed={selected}
      className={cn(
        "flex items-center gap-2.5 min-h-[44px] px-3 py-2 text-sm font-semibold text-start transition-all duration-brand-fast ease-brand border",
        selected
          ? "border-brand-sky bg-brand-sky/15 text-white"
          : "bg-white/[0.04] text-white/70 border-white/[0.1] hover:border-white/30 hover:text-white"
      )}>
      <FlagEmoji iso={isoOf(c)} size={22} className="shrink-0 ring-1 ring-white/15" />
      <span className="truncate">{translateCountry(c)}</span>
    </button>
  );

  const handleSubmit = () => {
    if (!pickup || !delivery) return;
    setQuoteData({
      pickupCountry: pickup,
      pickupCountryId: "",
      pickupCity: "",
      pickupCityId: "",
      deliveryCountry: delivery,
      deliveryCountryId: "",
      deliveryCity: "",
      deliveryCityId: "",
      items: [],
      preferredDate: "",
      flexibility: "2",
      earliestDate: "",
      latestDate: "",
    });
    navigate(localePath("/quote"));
  };

  return (
    <div className="glass p-5 sm:p-7 md:p-9 shadow-2xl">
      <h3 className="font-display text-base sm:text-xl font-bold text-white mb-1">{t('hero.quickQuote')}</h3>
      <p className="text-sm text-white/60 mb-7">{t('hero.quickQuoteSubtitle')}</p>

      <div className="mb-5">
        <span className="text-xs font-bold text-white/50 uppercase tracking-wider">{t('hero.from')}</span>
        <div className="mt-2.5 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {pickupCountryNames.map((c) => chip(c, pickup === c, () => handlePickup(c)))}
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="h-px flex-1 bg-white/10" />
        <button type="button" onClick={handleSwap} disabled={!canSwap}
          aria-label={t('hero.swapDirection', { defaultValue: 'Swap direction' })}
          className="flex items-center gap-2 min-h-[44px] px-3 border border-white/15 text-xs font-bold uppercase tracking-wider text-brand-sky hover:border-brand-sky disabled:opacity-30 disabled:hover:border-white/15 transition-colors">
          <ArrowLeftRight className="h-4 w-4" />
          {t('hero.swapDirection', { defaultValue: 'Swap direction' })}
        </button>
        <div className="h-px flex-1 bg-white/10" />
      </div>

      <div className="mb-7">
        <span className="text-xs font-bold text-white/50 uppercase tracking-wider">{t('hero.to')}</span>
        <div className="mt-2.5 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {pickup ? (
            deliveryCountryNames.length > 0 ? deliveryCountryNames.map((c) => chip(c, delivery === c, () => setDelivery(c))) : (
              <p className="col-span-full text-sm text-white/50 py-2">{t('hero.noDestinations')}</p>
            )
          ) : (
            <p className="col-span-full text-sm text-white/50 py-2">{t('hero.selectPickupFirst')}</p>
          )}
        </div>
      </div>

      <Button onClick={handleSubmit} disabled={!pickup || !delivery}
        className="w-full h-14 border-2 border-brand-orange bg-transparent text-brand-orange hover:bg-brand-orange/10 font-bold text-base sm:text-lg shadow-lg disabled:opacity-30">
        {t('hero.getQuotes')} <ArrowRight className="ml-2 h-5 w-5" />
      </Button>
    </div>
  );
};

/* ─── ANIMATED SECTIONS ─── */
const neonColors = ['blue', 'green', 'purple', 'yellow'] as const;

const HowItWorksSection = ({ steps }: { steps: { icon: any; num: string; title: string; desc: string; color: string }[] }) => {
  const { t } = useTranslation();
  const { ref, isRevealed } = useScrollReveal();
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!isRevealed) return;
    const id = setInterval(() => setActive((a) => (a + 1) % steps.length), 3200);
    return () => clearInterval(id);
  }, [isRevealed, steps.length]);

  return (
    <section className="py-12 md:py-28 bg-white">
      <div className="container max-w-5xl" ref={ref}>
        <div className="mb-8 md:mb-16" style={staggerStyle(0, isRevealed)}>
          <span className="section-badge-light">{t('hero.howItWorks')}</span>
          <h2 className="font-display section-heading text-brand-navy mt-6">{t('hero.howItWorksHeading')}</h2>
          <p className="text-muted-foreground mt-4 max-w-xl text-base sm:text-lg leading-relaxed">{t('hero.howItWorksSubtitle')}</p>
        </div>

        {/* Route-style timeline: vertical on mobile, horizontal on desktop */}
        <ol className="relative grid grid-cols-1 lg:grid-cols-4 gap-0 lg:gap-6">
          {/* track */}
          <span aria-hidden className="absolute start-[19px] top-5 bottom-5 w-px border-s-2 border-dashed border-brand-navy/15 lg:hidden" />
          <span aria-hidden className="hidden lg:block absolute top-5 start-5 end-5 h-px border-t-2 border-dashed border-brand-navy/15" />
          <span
            aria-hidden
            className="hidden lg:block absolute top-5 start-5 h-[2px] bg-brand-orange transition-all duration-700 ease-brand"
            style={{ width: `calc(${(active / (steps.length - 1)) * 100}% - ${active === steps.length - 1 ? 40 : 0}px)` }}
          />
          {steps.map((s, i) => {
            const StepIcon = s.icon;
            const done = i <= active;
            const isActive = i === active;
            return (
              <li
                key={s.title}
                style={staggerStyle(i + 1, isRevealed, 100)}
                className="relative flex lg:flex-col gap-5 lg:gap-6 pb-10 last:pb-0 lg:pb-0 cursor-pointer"
                onClick={() => setActive(i)}
              >
                <div
                  className={cn(
                    "relative z-10 shrink-0 w-10 h-10 flex items-center justify-center border-2 transition-all duration-brand-mid ease-brand",
                    isActive ? "bg-brand-navy border-brand-orange text-brand-orange scale-110"
                      : done ? "bg-brand-navy border-brand-navy text-brand-sky"
                      : "bg-white border-brand-navy/20 text-brand-grey-500"
                  )}
                >
                  <StepIcon className="h-[18px] w-[18px]" strokeWidth={1.8} />
                </div>
                <div className="pt-1 lg:pt-0">
                  <div className={cn("text-xs font-bold tracking-[0.2em] mb-2 transition-colors", isActive ? "text-brand-orange" : "text-brand-grey-500")}>
                    {s.num}
                  </div>
                  <h3 className="font-display font-bold text-brand-navy mb-2 text-base sm:text-lg">{s.title}</h3>
                  <p className={cn("text-[15px] leading-relaxed transition-colors duration-brand-mid", isActive ? "text-brand-grey-700" : "text-brand-grey-500")}>
                    {s.desc}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
};

const WhySection = ({ advantages, trustPoints }: { advantages: any[]; trustPoints: any[] }) => {
  const { t } = useTranslation();
  const { ref, isRevealed } = useScrollReveal();
  const accents = ['text-brand-sky', 'text-brand-teal', 'text-brand-violet', 'text-brand-orange'];

  return (
    <section className="py-12 md:py-28 bg-brand-off-white">
      <div className="container max-w-5xl" ref={ref}>
        <div className="max-w-2xl mb-8 md:mb-16" style={staggerStyle(0, isRevealed)}>
          <span className="section-badge-light">{t('hero.whyEuroCollis')}</span>
          <h2 className="font-display section-heading text-brand-navy mt-6 mb-4">{t('hero.whyHeading')}</h2>
          <p className="text-brand-grey-500 text-base sm:text-lg leading-relaxed">{t('hero.whySubtitle')}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-16">
          {/* Advantages: numbered ledger */}
          <ol className="border-t border-brand-navy/15">
            {advantages.map((a, i) => (
              <li key={a.title} style={staggerStyle(i + 1, isRevealed, 100)} className="flex gap-5 py-6 border-b border-brand-navy/15">
                <span className="font-mono text-xs font-bold tracking-[0.2em] text-brand-orange pt-1 w-6 shrink-0">0{i + 1}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1.5">
                    <a.icon className={cn("h-4 w-4", accents[i % 4])} strokeWidth={1.8} />
                    <h3 className="font-display font-bold text-brand-navy text-base">{a.title}</h3>
                  </div>
                  <p className="text-sm text-brand-grey-500 leading-relaxed">{a.desc}</p>
                </div>
              </li>
            ))}
          </ol>

          {/* Trust points: one navy panel, shared dividers */}
          <div className="bg-brand-navy grid grid-cols-1 sm:grid-cols-2 self-start" style={staggerStyle(2, isRevealed)}>
            {trustPoints.map((tp, i) => (
              <div
                key={tp.title}
                className={cn(
                  "p-4 md:p-7 border-white/10",
                  i > 0 && "border-t sm:border-t-0",
                  i % 2 === 1 && "sm:border-s",
                  i >= 2 && "sm:border-t"
                )}
              >
                <div className={cn("w-10 h-10 border flex items-center justify-center mb-5 border-current", accents[i % 4])}>
                  <tp.icon className="h-5 w-5" strokeWidth={1.6} />
                </div>
                <h3 className="font-display font-bold text-white mb-2 text-base">{tp.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{tp.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const TransporterSection = ({ localePath }: { localePath: (p: string) => string }) => {
  const { t } = useTranslation();
  const { ref, isRevealed } = useScrollReveal();
  const [active, setActive] = useState(0);

  const cards = [
    { icon: LayoutDashboard, titleKey: 'manageBusiness' },
    { icon: Target, titleKey: 'workEfficiently' },
    { icon: Store, titleKey: 'betterExperience' },
    { icon: UserCheck, titleKey: 'moreCustomers' },
  ];
  const ActiveIcon = cards[active].icon;

  return (
    <section className="py-12 md:py-28 bg-white">
      <div className="container max-w-5xl" ref={ref}>
        <div className="mb-10 md:mb-14" style={staggerStyle(0, isRevealed)}>
          <span className="section-badge-light">{t('transporterSection.badge')}</span>
          <h2 className="font-display section-heading text-brand-navy mt-6 mb-4">
            {t('transporterSection.heading')}{' '}
            <span className="text-brand-orange">{t('transporterSection.headingHighlight')}</span>
          </h2>
          <p className="text-brand-grey-500 text-base sm:text-lg max-w-2xl leading-relaxed">{t('transporterSection.subtitle')}</p>
        </div>

        <div className="border border-brand-navy/15" style={staggerStyle(1, isRevealed)}>
          {/* Module tabs */}
          <div role="tablist" className="grid grid-cols-4 border-b border-brand-navy/15">
            {cards.map((card, i) => (
              <button
                key={card.titleKey}
                role="tab"
                aria-selected={active === i}
                aria-label={t(`transporterSection.cards.${card.titleKey}.title`)}
                onClick={() => setActive(i)}
                className={cn(
                  "relative flex flex-col items-center justify-center gap-2 py-4 min-h-[64px] transition-colors duration-brand-fast ease-brand",
                  i > 0 && "border-s border-brand-navy/15",
                  active === i ? "bg-brand-navy text-brand-sky" : "text-brand-grey-500 hover:text-brand-navy"
                )}
              >
                <card.icon className="h-5 w-5" strokeWidth={1.8} />
                <span className="hidden sm:block text-xs font-semibold px-2 text-center leading-tight">
                  {t(`transporterSection.cards.${card.titleKey}.title`)}
                </span>
                {active === i && <span className="absolute bottom-0 inset-x-0 h-[3px] bg-brand-orange" />}
              </button>
            ))}
          </div>

          {/* Active module panel */}
          <div key={active} className="grid grid-cols-1 md:grid-cols-2 animate-fade-in">
            <div className="p-4 md:p-10">
              <div className="text-xs font-bold tracking-[0.2em] text-brand-orange mb-3">
                0{active + 1} / 04
              </div>
              <h3 className="font-display font-bold text-brand-navy text-2xl md:text-3xl mb-4">
                {t(`transporterSection.cards.${cards[active].titleKey}.title`)}
              </h3>
              <p className="text-brand-grey-500 text-base md:text-lg leading-relaxed">
                {t(`transporterSection.cards.${cards[active].titleKey}.desc`)}
              </p>
              <p className="text-sm text-brand-grey-500 mt-6 pt-6 border-t border-brand-navy/10">
                {t('transporterSection.subtitle2')}
              </p>
            </div>
            <div className="relative bg-brand-navy min-h-[200px] flex items-center justify-center overflow-hidden">
              <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(currentColor_1px,transparent_1px),linear-gradient(90deg,currentColor_1px,transparent_1px)] bg-[size:28px_28px] text-brand-sky" />
              <div className="relative flex flex-col items-center gap-4">
                <div className="w-20 h-20 border border-brand-sky flex items-center justify-center">
                  <ActiveIcon className="h-9 w-9 text-brand-sky" strokeWidth={1.4} />
                </div>
                <div className="flex gap-1.5">
                  {cards.map((_, i) => (
                    <span key={i} className={cn("h-1 transition-all duration-brand-mid", i === active ? "w-8 bg-brand-orange" : "w-3 bg-white/20")} />
                  ))}
                </div>
                <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/50">Courier OS</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10" style={staggerStyle(2, isRevealed)}>
          <Button
            className="border-2 border-brand-navy bg-brand-navy text-brand-orange hover:bg-brand-navy/90 font-bold h-14 px-6 sm:px-10 text-base sm:text-lg group w-full sm:w-auto"
            asChild
          >
            <Link to={localePath("/for-couriers")} className="flex items-center justify-center gap-2">
              <span className="hidden sm:inline">{t('transporterSection.ctaFull')}</span>
              <span className="sm:hidden">{t('transporterSection.ctaShort')}</span>
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 rtl:rotate-180 transition-transform duration-brand-fast ease-brand flex-shrink-0" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

const CustomRequestSection = ({ localePath }: { localePath: (p: string) => string }) => {
  const { t } = useTranslation();
  const { ref, isRevealed } = useScrollReveal();

  const customCards = [
    { icon: Package, key: 'large', accent: 'text-brand-sky' },
    { icon: Clock, key: 'express', accent: 'text-brand-teal' },
    { icon: MapPin, key: 'special', accent: 'text-brand-violet' },
    { icon: ShieldCheck, key: 'premium', accent: 'text-brand-warning' },
  ];

  return (
    <section className="py-12 md:py-28 bg-brand-off-white">
      <div className="container max-w-5xl" ref={ref}>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 lg:gap-16 items-start">
          <div className="lg:col-span-2" style={staggerStyle(0, isRevealed)}>
            <span className="section-badge-light">{t('hero.customRequests')}</span>
            <h2 className="font-display section-heading text-brand-navy mt-6">{t('hero.customHeading')}</h2>
            <p className="text-brand-grey-500 text-base sm:text-lg leading-relaxed mt-4">{t('hero.customSubtitle')}</p>
            <ul className="mt-6 space-y-3">
              {['multipleQuotes', 'specifyRequirements', 'chooseBest'].map((key) => (
                <li key={key} className="flex items-center gap-3 text-sm text-brand-grey-700">
                  <CheckCircle className="h-4 w-4 shrink-0 text-brand-navy" />
                  {t(`hero.customBullets.${key}`)}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3 bg-brand-navy" style={staggerStyle(1, isRevealed)}>
            <div className="px-6 pt-6 pb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-white/40">
              {t('hero.postCustomRequest')}
            </div>
            <ul>
              {customCards.map((card) => (
                <li key={card.key} className="border-t border-white/10">
                  <Link
                    to={localePath("/custom-request")}
                    className="flex items-center gap-4 px-6 py-5 min-h-[44px] group hover:bg-white/[0.04] transition-colors duration-brand-fast ease-brand"
                  >
                    <card.icon className={`h-6 w-6 shrink-0 ${card.accent}`} strokeWidth={1.6} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <h3 className="font-display font-bold text-white text-base">{t(`hero.customCards.${card.key}.title`)}</h3>
                        <span className={`text-[11px] font-semibold uppercase tracking-wider ${card.accent}`}>{t(`hero.customCards.${card.key}.badge`)}</span>
                      </div>
                      <p className="text-sm text-white/55 mt-0.5">{t(`hero.customCards.${card.key}.desc`)}</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-white/30 group-hover:text-brand-orange group-hover:translate-x-1 rtl:rotate-180 transition-all duration-brand-fast ease-brand" />
                  </Link>
                </li>
              ))}
            </ul>
            <div className="p-4 sm:p-6 border-t border-white/10">
              <Button className="w-full border-2 border-brand-orange bg-transparent text-brand-orange hover:bg-brand-orange/10 font-bold h-12 text-base" asChild>
                <Link to={localePath("/custom-request")}>{t('hero.postCustomRequest')} <ArrowRight className="ms-2 h-4 w-4 rtl:rotate-180" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};


const FaqSection = ({ faqs, expandedFaqCard, toggleFaqCard, localePath }: { faqs: { q: string; a: string }[]; expandedFaqCard: number | null; toggleFaqCard: (i: number) => void; localePath: (p: string) => string }) => {
  const { t } = useTranslation();
  const { ref, isRevealed } = useScrollReveal();

  return (
    <section className="py-12 md:py-28 bg-brand-navy">
      <div className="container max-w-5xl" ref={ref}>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 lg:gap-16">
          <div className="lg:col-span-2" style={staggerStyle(0, isRevealed)}>
            <span className="section-badge-dark">{t('hero.faqSection')}</span>
            <h2 className="font-display section-heading text-white mt-6">{t('hero.faqHeading')}</h2>
            <p className="text-white/60 mt-4 text-base sm:text-lg leading-relaxed">{t('hero.faqSubtitle')}</p>
            <Button variant="outline" className="hidden lg:inline-flex mt-8 font-semibold h-12 px-6 text-base border-white/20 text-white hover:bg-white/10 bg-transparent" asChild>
              <Link to={localePath("/faq")}>{t('hero.viewAllQuestions')} <ArrowRight className="ms-2 h-4 w-4 rtl:rotate-180" /></Link>
            </Button>
          </div>

          <ul className="lg:col-span-3 border-t border-white/10" style={staggerStyle(1, isRevealed)}>
            {faqs.map((faq, i) => {
              const open = expandedFaqCard === i;
              return (
                <li key={i} className="border-b border-white/10">
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => toggleFaqCard(i)}
                    className="w-full flex items-start gap-4 py-5 min-h-[44px] text-start group"
                  >
                    <span className={cn("font-mono text-xs font-bold tracking-[0.2em] pt-1 w-6 shrink-0 transition-colors", open ? "text-brand-orange" : "text-white/35")}>
                      0{i + 1}
                    </span>
                    <span className="flex-1 font-display font-bold text-white text-base leading-snug group-hover:text-brand-sky transition-colors">
                      {faq.q}
                    </span>
                    <ChevronDown className={cn("h-5 w-5 shrink-0 mt-0.5 transition-transform duration-200", open ? "rotate-180 text-brand-orange" : "text-white/40")} aria-hidden="true" />
                  </button>
                  <div className={cn("grid transition-all duration-300 ease-brand", open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0")}>
                    <p className="overflow-hidden ps-10 pe-9 text-sm text-white/65 leading-relaxed">
                      <span className="block pb-5">{faq.a}</span>
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="mt-10 lg:hidden">
          <Button variant="outline" className="w-full font-semibold h-12 px-8 text-base border-white/20 text-white hover:bg-white/10 bg-transparent" asChild>
            <Link to={localePath("/faq")}>{t('hero.viewAllQuestions')} <ArrowRight className="ms-2 h-4 w-4 rtl:rotate-180" /></Link>
          </Button>
        </div>
      </div>
    </section>
  );
};


const FinalCtaSection = ({ localePath }: { localePath: (p: string) => string }) => {
  const { t } = useTranslation();
  const { ref, isRevealed } = useScrollReveal();

  return (
    <section className="py-12 md:py-32 bg-brand-navy">
      <div className="container max-w-2xl text-center space-y-5 sm:space-y-8" ref={ref}>
        <h2 className="font-display section-heading text-white" style={staggerStyle(0, isRevealed)}>{t('hero.readyToShip')}</h2>
        <p className="text-white/60 max-w-md mx-auto leading-relaxed text-base sm:text-lg" style={staggerStyle(1, isRevealed)}>{t('hero.readySubtitle')}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center" style={staggerStyle(2, isRevealed)}>
          <Button className="w-full sm:w-auto border-2 border-brand-orange bg-brand-navy text-brand-orange hover:bg-brand-navy/90 font-bold h-12 px-8 text-base shadow-lg" asChild>
            <Link to={localePath("/quote")}>{t('hero.getQuote')} <ArrowRight className="ml-2 h-5 w-5" /></Link>
          </Button>
          <Button className="w-full sm:w-auto h-12 px-8 text-base font-semibold border border-white/20 text-white hover:bg-white/10 bg-white/[0.06]" asChild>
            <Link to={localePath("/for-couriers")}>{t('hero.becomeTransporterCta')}</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

const Index = () => {
  const { t } = useTranslation();
  const { localePath } = useLocalizedPath();
  const [expandedFaqCard, setExpandedFaqCard] = useState<number | null>(null);

  const steps = [
    { icon: Package, num: "01", title: t('hero.steps.describe.title'), desc: t('hero.steps.describe.desc'), color: "orange" },
    { icon: Search, num: "02", title: t('hero.steps.compare.title'), desc: t('hero.steps.compare.desc'), color: "blue" },
    { icon: CalendarCheck, num: "03", title: t('hero.steps.book.title'), desc: t('hero.steps.book.desc'), color: "green" },
    { icon: Truck, num: "04", title: t('hero.steps.relax.title'), desc: t('hero.steps.relax.desc'), color: "purple" },
  ];

  const trustPoints = [
    { icon: BadgeCheck, morphTo: CheckCircle, title: t('hero.trust.verified.title'), desc: t('hero.trust.verified.desc'), color: "green" },
    { icon: Eye, morphTo: Search, title: t('hero.trust.transparent.title'), desc: t('hero.trust.transparent.desc'), color: "blue" },
    { icon: Lock, morphTo: ShieldCheck, title: t('hero.trust.securePay.title'), desc: t('hero.trust.securePay.desc'), color: "purple" },
    { icon: Shield, morphTo: ShieldCheck, title: t('hero.trust.insurance.title'), desc: t('hero.trust.insurance.desc'), color: "orange" },
  ];

  const advantages = [
    { icon: Globe, title: t('hero.advantages.marketplace.title'), desc: t('hero.advantages.marketplace.desc'), color: "blue" },
    { icon: Scale, title: t('hero.advantages.rates.title'), desc: t('hero.advantages.rates.desc'), color: "green" },
    { icon: Route, title: t('hero.advantages.flexible.title'), desc: t('hero.advantages.flexible.desc'), color: "purple" },
    { icon: Headphones, title: t('hero.advantages.humanSupport.title'), desc: t('hero.advantages.humanSupport.desc'), color: "orange" },
  ];

  const faqs = [
    { q: t('hero.faqs.howWorks.q'), a: t('hero.faqs.howWorks.a') },
    { q: t('hero.faqs.whoPays.q'), a: t('hero.faqs.whoPays.a') },
    { q: t('hero.faqs.whatItems.q'), a: t('hero.faqs.whatItems.a') },
    { q: t('hero.faqs.cancel.q'), a: t('hero.faqs.cancel.a') },
    { q: t('hero.faqs.insured.q'), a: t('hero.faqs.insured.a') },
    { q: t('hero.faqs.howLong.q'), a: t('hero.faqs.howLong.a') },
  ];


  const seoJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SoftwareApplication",
        "name": "EuroCollis",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web, Android, iOS",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "EUR"
        }
      }
    ]
  };

  useEffect(() => {
    const handleScroll = () => {
      if (expandedFaqCard !== null) {
        setExpandedFaqCard(null);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [expandedFaqCard]);

  const toggleFaqCard = (index: number) => {
    setExpandedFaqCard(expandedFaqCard === index ? null : index);
  };

  return (
    <div>
      <SEOHead
        title={t('seo.homeTitle', t('hero.pageTitle'))}
        description={t('seo.homeDesc', t('hero.subtitle').slice(0, 155))}
        canonical="/"
        jsonLd={seoJsonLd}
        keywords={t('seo.homeKeywords', '')}
      />
      {/* ── HERO (DARK) ── */}
      <section className="relative overflow-hidden -mt-[72px] bg-brand-navy">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,rgba(255,255,255,0.04)_0%,transparent_60%)]" />
        
        <div className="container relative pt-12 md:pt-24 lg:pt-28 pb-16 md:pb-20 lg:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-20 items-start lg:items-center">
            <div className="space-y-5 sm:space-y-8 pt-4">
              <DirectionBadge />
              <div>
                <h1 className="font-display hero-heading text-white">
                  {t('hero.headline')}
                  <br />
                  <RotatingWord />
                </h1>
              </div>
              <p className="text-base sm:text-lg text-white/70 max-w-lg leading-relaxed">
                {t('hero.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button className="w-full border-2 border-brand-orange bg-brand-navy text-brand-orange hover:bg-brand-navy/90 font-bold h-12 px-8 text-base shadow-lg" asChild>
                  <Link to={localePath("/quote")}>{t('hero.getQuote')} <ArrowRight className="ml-2 h-5 w-5" /></Link>
                </Button>
                <Button className="w-full h-12 px-8 text-base font-semibold border border-white/20 text-white hover:bg-white/10 bg-white/[0.06]" asChild>
                  <Link to={localePath("/for-couriers")}>{t('hero.becomeCourier')}</Link>
                </Button>
              </div>
            </div>
            <QuickQuoteWidget />
          </div>
        </div>
      </section>


      {/* ── LAUNCH PROMO BANNER ── */}
      <LaunchBanner />

      {/* ── HOW IT WORKS (LIGHT) ── */}
      <HowItWorksSection steps={steps} />


      {/* ── LIVE PLATFORM ACTIVITY (DARK) ── */}
      <LivePlatformActivity />

      {/* ── MEET MALAK (LIGHT) ── */}
      <MeetMalak />

      {/* ── WHY EUROCOLLIS (DARK) ── */}
      <WhySection advantages={advantages} trustPoints={trustPoints} />

      {/* ── ANIMATED ROUTE MAP (DARK) ── */}
      <AnimatedRouteMap />

      {/* ── PREMIUM TRANSPORTER SECTION (LIGHT) ── */}
      <TransporterSection localePath={localePath} />

      {/* ── FAQ (DARK) ── */}
      <FaqSection faqs={faqs} expandedFaqCard={expandedFaqCard} toggleFaqCard={toggleFaqCard} localePath={localePath} />

      {/* ── CUSTOM REQUEST (LIGHT) ── */}
      <CustomRequestSection localePath={localePath} />

      {/* ── FINAL CTA (DARK) ── */}
      <FinalCtaSection localePath={localePath} />
    </div>
  );
};

export default Index;
